/*
  Quick-add popup loading (VaultifyUK, 2026-11-02)
  - The popup fetches the product's slim "quick-add" view (templates/product.quick-add.json, no header/footer, only the
    blocks the popup shows) instead of the whole product page: about 1.2 MB down to about 125 KB.
  - The fetch starts as soon as the shopper hovers, focuses or touches the options button, and the response is kept for
    a short while, so by the time they click the popup usually has everything it needs.
  - The product section's stylesheets and its style block are added to the page once and reused, instead of being
    re-inserted on every opening. Re-inserting them made the browser restyle the whole page each time, which was the
    largest part of the delay. When a prefetch finishes, they are added while the browser is idle, before the click.
*/
window.VaultifyQuickAdd = window.VaultifyQuickAdd || (function () {
  const VIEW = 'quick-add';
  const MAX_AGE = 30000; // keep a prefetched popup for 30s so stock and price stay fresh
  const cache = new Map();
  const addedStyles = new Set();

  function viewUrl(productUrl) {
    return `${productUrl}${productUrl.includes('?') ? '&' : '?'}view=${VIEW}`;
  }

  function idle(callback) {
    if ('requestIdleCallback' in window) requestIdleCallback(callback, { timeout: 1500 });
    else setTimeout(callback, 50);
  }

  function hasStylesheet(href) {
    return Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some((link) => link.href === href);
  }

  // Moves a product section's stylesheets and <style> blocks out of `root` and into the page, once each.
  // `sectionId`: when given, ids in the style text are rewritten the way QuickAddModal.preventDuplicatedIDs() does,
  // so the text matches what the popup itself would have added.
  function hoistStyles(root, sectionId) {
    if (!root) return;
    root.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
      const href = new URL(link.getAttribute('href'), window.location.href).href;
      if (!hasStylesheet(href)) {
        const sheet = document.createElement('link');
        Array.from(link.attributes).forEach((attribute) => sheet.setAttribute(attribute.name, attribute.value));
        sheet.setAttribute('data-quick-add-style', '');
        document.body.appendChild(sheet);
      }
      link.remove();
    });
    root.querySelectorAll('style').forEach((style) => {
      let css = style.textContent;
      if (sectionId) css = css.replaceAll(sectionId, `quickadd-${sectionId}`);
      if (!addedStyles.has(css)) {
        addedStyles.add(css);
        const tag = document.createElement('style');
        tag.setAttribute('data-quick-add-style', '');
        tag.textContent = css;
        document.body.appendChild(tag);
      }
      style.remove();
    });
  }

  function warmStyles(text) {
    idle(() => {
      const productElement = new DOMParser().parseFromString(text, 'text/html').querySelector('product-info');
      if (productElement) hoistStyles(productElement, productElement.dataset.section);
    });
  }

  function load(productUrl, { warm = false } = {}) {
    if (!productUrl) return Promise.reject(new Error('No product URL'));
    const entry = cache.get(productUrl);
    if (entry && Date.now() - entry.time < MAX_AGE) return entry.promise;

    const promise = fetch(viewUrl(productUrl))
      .then((response) => {
        if (!response.ok) throw new Error(`Quick-add view returned ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (!text.includes('<product-info')) throw new Error('Quick-add view had no product-info');
        return text;
      })
      // If the slim view is missing for any reason, fall back to the full product page (the original behaviour).
      .catch(() => fetch(productUrl).then((response) => response.text()));

    promise.catch(() => cache.delete(productUrl));
    if (warm) promise.then(warmStyles).catch(() => {});
    cache.set(productUrl, { promise, time: Date.now() });
    return promise;
  }

  function take(productUrl) {
    const promise = load(productUrl);
    cache.delete(productUrl); // each opening uses a response once; the next opening fetches fresh
    return promise;
  }

  function prefetchFrom(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('modal-opener [data-product-url]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;
    load(button.getAttribute('data-product-url'), { warm: true }).catch(() => {});
  }

  document.addEventListener(
    'pointerover',
    (event) => {
      if (event.pointerType === 'mouse') prefetchFrom(event);
    },
    { passive: true }
  );
  document.addEventListener('touchstart', prefetchFrom, { passive: true });
  document.addEventListener('focusin', prefetchFrom);

  return { VIEW, load, take, hoistStyles };
})();

if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        window.VaultifyQuickAdd.take(opener.getAttribute('data-product-url'))
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        window.VaultifyQuickAdd.hoistStyles(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
